import React from 'react';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/ui';

export type IntegrityStatus =
  | 'online'
  | 'offline'
  | 'connecting'
  | 'error'
  | 'ready'
  | 'nominal'
  | 'warning'
  | 'critical';

export interface StatusIndicatorProps {
  status: IntegrityStatus;
  label?: string;
  pulse?: boolean;
  className?: string;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  pulse = true,
  className
}) => {
  const statusConfig = {
    online: {
      color: 'bg-state-nominal',
      variant: 'success' as const,
      defaultLabel: 'Online'
    },
    nominal: {
      color: 'bg-state-nominal',
      variant: 'success' as const,
      defaultLabel: 'Nominal'
    },
    offline: {
      color: 'bg-state-neutral',
      variant: 'default' as const,
      defaultLabel: 'Offline'
    },
    connecting: {
      color: 'bg-state-warning',
      variant: 'warning' as const,
      defaultLabel: 'Connecting'
    },
    warning: {
      color: 'bg-state-warning',
      variant: 'warning' as const,
      defaultLabel: 'Warning'
    },
    error: {
      color: 'bg-state-critical',
      variant: 'danger' as const,
      defaultLabel: 'Error'
    },
    critical: {
      color: 'bg-state-critical',
      variant: 'danger' as const,
      defaultLabel: 'Critical'
    },
    ready: {
      color: 'bg-state-nominal',
      variant: 'info' as const,
      defaultLabel: 'Ready'
    }
  };

  const config = statusConfig[status];
  const displayLabel = label || config.defaultLabel;
  const shouldPulse = pulse && status !== 'offline' && status !== 'nominal';

  return (
    <div className={cn('flex items-center gap-2', className)} role="status" aria-label={displayLabel}>
      <div className="relative">
        <span
          className={cn(
            'inline-block size-2 rounded-full',
            config.color,
            shouldPulse && 'animate-status-breathe'
          )}
        />
        {status === 'connecting' && (
          <span className="absolute inset-0 size-2 animate-ping rounded-full bg-state-warning opacity-40" />
        )}
      </div>
      <Badge variant={config.variant} size="sm">
        {displayLabel}
      </Badge>
    </div>
  );
};

export { StatusIndicator };
